import { CircleStackIcon } from '@heroicons/react/24/outline';
import type { LaufwerkStatus } from '../../api/client';
import { indexFortschritt, statusLabel, statusZeilen, STATUS_BADGE, zahl } from '../../utils/laufwerk';

/** Karte „Index": Fortschrittsbalken, Zahlen je Status, letzte Fehler. */
export default function IndexKarte({ status }: { status: LaufwerkStatus | null }) {
  const f = status ? indexFortschritt(status.zahlen, status.gesamt) : null;
  const zeilen = status ? statusZeilen(status.zahlen) : [];
  return (
    <div className="card space-y-4">
      <div>
        <h2 className="page-h1 flex items-center gap-2"><CircleStackIcon className="h-5 w-5 text-ps-dark-400" /> Index</h2>
        <p className="mt-1 max-w-2xl text-xs leading-relaxed text-ps-dark-400">
          So viele Dateien sind schon durchsuchbar. Die Anzeige aktualisiert sich alle 15 Sekunden von selbst.
        </p>
      </div>

      {!status || !f ? (
        <div className="space-y-2"><div className="shimmer h-3 w-full rounded" /><div className="shimmer h-3 w-2/3 rounded" /></div>
      ) : status.gesamt === 0 ? (
        <div className="rounded-lg border border-dashed border-ps-dark-200 px-4 py-6 text-center text-sm text-ps-dark-500">
          Noch keine Dateien gemeldet.
        </div>
      ) : (
        <>
          <div>
            <div className="mb-1 flex items-baseline justify-between gap-2 text-sm">
              <span className="text-ps-dark-700"><span className="numerical font-semibold text-ps-dark-900">{zahl(f.fertig)}</span> von <span className="numerical">{zahl(f.nenner)}</span> Dateien indexiert</span>
              <span className="numerical font-semibold text-ps-dark-900">{f.prozent} %</span>
            </div>
            <div className="h-3 w-full overflow-hidden rounded-full bg-ps-dark-100" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={f.prozent} aria-label="Index-Fortschritt">
              <div className="h-full rounded-full bg-primary-500 transition-all duration-500" style={{ width: `${f.prozent}%` }} />
            </div>
            {f.wartend > 0 && <div className="mt-1 text-xs text-ps-dark-500">{zahl(f.wartend)} {f.wartend === 1 ? 'Datei wartet' : 'Dateien warten'} noch auf die Verarbeitung.</div>}
          </div>

          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {zeilen.map(({ status: s, n }) => (
              <li key={s} className="flex items-center justify-between gap-2 rounded-lg border border-ps-dark-100 px-3 py-2 text-sm">
                <span className={`badge ${STATUS_BADGE[s] ?? 'badge-neutral'}`}>{statusLabel(s)}</span>
                <span className="numerical font-medium text-ps-dark-800">{zahl(n)}</span>
              </li>
            ))}
          </ul>
          {((status.zahlen.nicht_unterstuetzt ?? 0) > 0 || (status.zahlen.zu_gross ?? 0) > 0) && (
            <p className="text-[11px] leading-relaxed text-ps-dark-400">
              «Nicht unterstützt» und «Zu gross» werden nicht indexiert (nur der Dateiname ist suchbar) und zählen deshalb nicht in den Fortschritt.
            </p>
          )}
        </>
      )}

      {status && status.fehler.length > 0 && (
        <div>
          <h3 className="mb-1.5 text-sm font-semibold text-rose-700">Letzte Fehler</h3>
          <ul className="divide-y divide-ps-dark-100 rounded-lg border border-rose-200 bg-rose-50/40">
            {status.fehler.map((e, i) => (
              <li key={`${e.pfad}#${i}`} className="px-3 py-2">
                <div className="break-all font-mono text-[11.5px] text-ps-dark-800">{e.pfad}</div>
                <div className="mt-0.5 break-words text-xs text-rose-700">{e.fehler || 'Unbekannter Fehler'}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
